import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiAxios from 'helpers/apiAxios';

const BASE = '/changelog_entries';

export const fetchAllChangelogEntries = createAsyncThunk(
  'changelogEntry/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}?per_page=100&all=true`);
      return res.data?.data || [];
    } catch (e) {
      return rejectWithValue(e.response?.data);
    }
  }
);

export const createChangelogEntry = createAsyncThunk(
  'changelogEntry/create',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(BASE, { changelog_entry: payload });
      return res.data?.data;
    } catch (e) {
      return rejectWithValue(e.response?.data);
    }
  }
);

export const updateChangelogEntry = createAsyncThunk(
  'changelogEntry/update',
  async ({ version, payload }, { rejectWithValue }) => {
    try {
      const res = await apiAxios.patch(`${BASE}/${version}`, { changelog_entry: payload });
      return res.data?.data;
    } catch (e) {
      return rejectWithValue(e.response?.data);
    }
  }
);

export const publishChangelogEntry = createAsyncThunk(
  'changelogEntry/publish',
  async (version, { rejectWithValue }) => {
    try {
      const res = await apiAxios.patch(`${BASE}/${version}/publish`);
      return res.data?.data;
    } catch (e) {
      return rejectWithValue(e.response?.data);
    }
  }
);

export const unpublishChangelogEntry = createAsyncThunk(
  'changelogEntry/unpublish',
  async (version, { rejectWithValue }) => {
    try {
      const res = await apiAxios.patch(`${BASE}/${version}/unpublish`);
      return res.data?.data;
    } catch (e) {
      return rejectWithValue(e.response?.data);
    }
  }
);

export const deleteChangelogEntry = createAsyncThunk(
  'changelogEntry/delete',
  async (version, { rejectWithValue }) => {
    try {
      await apiAxios.delete(`${BASE}/${version}`);
      return version;
    } catch (e) {
      return rejectWithValue(e.response?.data);
    }
  }
);

// ── helpers ───────────────────────────────────────────────────────────────────
const upsert = (list, updated) =>
  list.map(e => e.attributes?.version === updated.attributes?.version ? updated : e);

const changelogEntrySlice = createSlice({
  name: 'changelogEntry',
  initialState: { data: [], loading: false, saving: false, error: null },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchAllChangelogEntries.pending,  s => { s.loading = true; s.error = null; })
      .addCase(fetchAllChangelogEntries.fulfilled, (s, a) => { s.loading = false; s.data = a.payload; })
      .addCase(fetchAllChangelogEntries.rejected,  (s, a) => { s.loading = false; s.error = a.payload; })

      .addCase(createChangelogEntry.pending,   s => { s.saving = true; s.error = null; })
      .addCase(createChangelogEntry.fulfilled, (s, a) => { s.saving = false; s.data = [a.payload, ...s.data]; })
      .addCase(createChangelogEntry.rejected,  (s, a) => { s.saving = false; s.error = a.payload; })

      .addCase(updateChangelogEntry.pending,   s => { s.saving = true; s.error = null; })
      .addCase(updateChangelogEntry.fulfilled, (s, a) => { s.saving = false; s.data = upsert(s.data, a.payload); })
      .addCase(updateChangelogEntry.rejected,  (s, a) => { s.saving = false; s.error = a.payload; })

      .addCase(publishChangelogEntry.fulfilled,   (s, a) => { s.data = upsert(s.data, a.payload); })
      .addCase(unpublishChangelogEntry.fulfilled, (s, a) => { s.data = upsert(s.data, a.payload); })

      .addCase(deleteChangelogEntry.fulfilled, (s, a) => {
        s.data = s.data.filter(e => e.attributes?.version !== a.payload);
      });
  }
});

export default changelogEntrySlice.reducer;
